import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DUPLICATE_REPORT_REQUEST } from '../../../constants/Report';
import {
    getDuplicateReportDisplayStatus,
    getDuplicateReportError,
} from '../../../selectors/ReportHeader';
import * as ReportMetadata from '../../../selectors/ReportMetadata';
import * as ReportScreenConstants from '../../../constants/ReportScreen';

import Loader from '../../Loader/Loader';
import ReportAttributesInputs from './ReportAttributesInputs';
import ReportActionsModalContent from './ReportActionsModalContent';

type Props = {
    onClose: () => void;
};

const DuplicateReportModalContent = ({ onClose }: Props) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const updateReportDisplayStatus = useSelector(
        getDuplicateReportDisplayStatus,
    );
    const updateReportError = useSelector(getDuplicateReportError);
    const initialReportTitle = useSelector(ReportMetadata.getReportTitle);
    const initialReportDescription = useSelector(
        ReportMetadata.getReportDescription,
    );
    const [reportTitle, setReportTitle] = useState(
        intl.formatMessage({ id: 'copyOf' }, { title: initialReportTitle }),
    );
    const [reportDescription, setReportDescription] = useState(
        initialReportDescription,
    );
    // used to trigger close modal after updating is done
    const [requestDispatched, setRequestDispatched] = useState(false);

    const requestDuplicateReport = () => {
        dispatch({
            type: DUPLICATE_REPORT_REQUEST,
            payload: {
                reportTitle,
                reportDescription,
            },
        });
        setRequestDispatched(true);
    };

    useEffect(() => {
        if (
            updateReportDisplayStatus ===
                ReportScreenConstants.DUPLICATE_REPORT_STATUS_AVAILABLE &&
            requestDispatched
        ) {
            onClose();
        }
        return () => {
            onClose();
        };
    }, [onClose, updateReportDisplayStatus, requestDispatched]);

    let content;
    switch (updateReportDisplayStatus) {
        case ReportScreenConstants.DUPLICATE_REPORT_STATUS_ERROR:
            content = <div>{updateReportError}</div>;
            break;
        case ReportScreenConstants.DUPLICATE_REPORT_STATUS_LOADING:
            content = <Loader />;
            break;
        case ReportScreenConstants.DUPLICATE_REPORT_STATUS_AVAILABLE:
        default:
            content = (
                <ReportAttributesInputs
                    reportTitle={reportTitle}
                    onReportTitleChange={setReportTitle}
                    reportDescription={reportDescription}
                    onReportDescriptionChange={setReportDescription}
                />
            );
    }

    return (
        <ReportActionsModalContent
            header={intl.formatMessage({
                id: 'report.duplicateReport.duplicateHeader',
            })}
            onClose={onClose}
            mainButton={{
                action: requestDuplicateReport,
                text: intl.formatMessage({ id: 'done' }),
                disabled:
                    initialReportTitle === reportTitle &&
                    initialReportDescription === reportDescription,
            }}
        >
            {content}
        </ReportActionsModalContent>
    );
};

export default DuplicateReportModalContent;
